import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { 
  User as FirebaseUser, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged
} from 'firebase/auth';
import { ref, get } from 'firebase/database';
import { auth, database } from '../firebase/config';
import { User, UserProfile, Empresa } from '../types/User';
import { useSessionManager } from '../hooks/useSessionManager';
import { firebaseService } from '../services/firebaseService';
import { Permissoes } from '../types/Permission';

interface AuthContextType {
  currentUser: FirebaseUser | null;
  userProfile: UserProfile | null;
  empresa: Empresa | null;
  loading: boolean;
  login: (email: string, password: string, cnpj: string) => Promise<void>;
  logout: () => Promise<void>;
  reloadPermissions: () => Promise<void>;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [empresa, setEmpresa] = useState<Empresa | null>(null);
  const [loading, setLoading] = useState(true);

  const isAdmin = userProfile?.cargo === 'administrador' || userProfile?.cargo === 'admin';

  // Gerenciar sessão com renovação automática de token
  useSessionManager({ user: currentUser });

  const login = async (email: string, password: string, cnpj: string) => {
    try {
      console.log('🔐 Iniciando login...', { email, cnpj });
      
      // Fazer login do usuário no Firebase Auth
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      console.log('✅ Firebase Auth OK:', user.uid);
      
      // Buscar dados da empresa
      const empresaRef = ref(database, `empresas/${cnpj}`);
      console.log('🔍 Buscando empresa:', `empresas/${cnpj}`);
      const empresaSnapshot = await get(empresaRef);
      
      if (!empresaSnapshot.exists()) {
        console.log('❌ Empresa não encontrada');
        await signOut(auth);
        throw new Error('Empresa não encontrada. Verifique o CNPJ.');
      }
      
      const empresaData = empresaSnapshot.val() as Empresa;
      console.log('✅ Empresa encontrada:', empresaData.nome || empresaData.cnpj);
      
      // Buscar perfil do usuário na empresa específica
      const userRef = ref(database, `empresas/${cnpj}/usuarios/${user.uid}`);
      console.log('🔍 Buscando usuário:', `empresas/${cnpj}/usuarios/${user.uid}`);
      const userSnapshot = await get(userRef);
      
      if (!userSnapshot.exists()) {
        console.log('❌ Usuário não encontrado na empresa');
        await signOut(auth);
        throw new Error('Usuário não encontrado nesta empresa.');
      }
      
      const userData = userSnapshot.val() as User;
      console.log('✅ Usuário encontrado:', userData.nome, 'Cargo:', userData.cargo);
      
      // Buscar permissões do usuário
      let permissoes: Permissoes | null = null;
      try {
        permissoes = await firebaseService.getPermissoesUsuario(cnpj, user.uid);
        console.log('✅ Permissões carregadas:', permissoes ? 'Sim' : 'Não');
      } catch (error) {
        console.warn('⚠️ Erro ao carregar permissões:', error);
        // Continuar mesmo sem permissões
      }
      
      const profile: UserProfile = {
        id: userData.id,
        nome: userData.nome,
        email: userData.email,
        cargo: userData.cargo,
        empresaId: userData.empresaId, // Este é o CNPJ
        empresaCnpj: cnpj,
        bAtivo: userData.bAtivo,
        fotoUrl: userData.fotoUrl,
        perfilAcessoId: userData.perfilAcessoId,
        permissoes: permissoes || undefined
      };
      
      // Verificar se é administrador
      if (profile.cargo !== 'administrador' && profile.cargo !== 'admin') {
        console.log('❌ Usuário não é administrador:', profile.cargo);
        await signOut(auth);
        throw new Error('Acesso negado. Apenas administradores podem acessar o painel.');
      }
      
      console.log('✅ Login realizado com sucesso!');
      setUserProfile(profile);
      setEmpresa(empresaData);
      
      // Salvar dados no localStorage para persistência
      localStorage.setItem('userProfile', JSON.stringify(profile));
      localStorage.setItem('empresa', JSON.stringify(empresaData));
    } catch (error) {
      console.error('❌ Erro no login:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setUserProfile(null);
      setEmpresa(null);
      
      // Limpar dados do localStorage
      localStorage.removeItem('userProfile');
      localStorage.removeItem('empresa');
    } catch (error) {
      console.error('Erro no logout:', error);
      throw error;
    }
  };

  const reloadPermissions = async () => {
    if (!currentUser || !userProfile?.empresaCnpj) {
      console.warn('⚠️ Não é possível recarregar permissões: usuário não autenticado');
      return;
    }

    try {
      console.log('🔄 Recarregando permissões...');
      
      // Buscar permissões atualizadas
      const permissoes = await firebaseService.getPermissoesUsuario(
        userProfile.empresaCnpj,
        currentUser.uid
      );

      // Atualizar perfil com novas permissões
      const updatedProfile: UserProfile = {
        ...userProfile,
        permissoes: permissoes || undefined
      };

      setUserProfile(updatedProfile);
      
      // Atualizar localStorage
      localStorage.setItem('userProfile', JSON.stringify(updatedProfile));
      
      console.log('✅ Permissões recarregadas com sucesso!');
    } catch (error) {
      console.error('❌ Erro ao recarregar permissões:', error);
      throw error;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('🔄 Auth state changed:', !!user);
      
      if (user) {
        setCurrentUser(user);
        
        // Tentar recuperar o perfil do usuário se não estiver carregado
        if (!userProfile) {
          try {
            // Verificar se há dados salvos no localStorage
            const savedProfile = localStorage.getItem('userProfile');
            const savedEmpresa = localStorage.getItem('empresa');
            
            if (savedProfile && savedEmpresa) {
              const profile = JSON.parse(savedProfile);
              const empresa = JSON.parse(savedEmpresa);
              
              // Verificar se o perfil ainda é válido (mesmo UID)
              if (profile.id === user.uid) {
                setUserProfile(profile);
                setEmpresa(empresa);
                console.log('🔄 Perfil recuperado do localStorage');
              } else {
                console.log('❌ Perfil inválido, limpando localStorage');
                // Limpar dados inválidos
                localStorage.removeItem('userProfile');
                localStorage.removeItem('empresa');
                setUserProfile(null);
                setEmpresa(null);
              }
            } else {
              console.log('❌ Nenhum perfil salvo encontrado');
              setUserProfile(null);
              setEmpresa(null);
            }
          } catch (error) {
            console.error('❌ Erro ao recuperar perfil:', error);
            localStorage.removeItem('userProfile');
            localStorage.removeItem('empresa');
            setUserProfile(null);
            setEmpresa(null);
          }
        }
      } else {
        console.log('❌ Usuário não autenticado');
        setCurrentUser(null);
        setUserProfile(null);
        setEmpresa(null);
        // Limpar dados do localStorage quando não há usuário
        localStorage.removeItem('userProfile');
        localStorage.removeItem('empresa');
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []); // Remover userProfile da dependência para evitar loops

  const value: AuthContextType = {
    currentUser,
    userProfile,
    empresa,
    loading,
    login,
    logout,
    reloadPermissions,
    isAdmin
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
